// EI (Emirates Islamic) — Shariah-compliant cards. Amazon (2131) and RTA (0002)
// share identical layout.
// Columns: postDate DD@47 MMM@61 | trxnDate DD@103 MMM@118 | desc@152–261 | amount@~516
// Summary footer (y≈255) repeats on every page.
import { toNum, amountWithSign } from '../util.js';

const MONTHS = { JAN:'01',FEB:'02',MAR:'03',APR:'04',MAY:'05',JUN:'06',JUL:'07',AUG:'08',SEP:'09',OCT:'10',NOV:'11',DEC:'12' };

// "1st May 2026" → YYYY-MM-DD
const parseOrdinal = (s) => {
  const m = String(s).match(/(\d{1,2})(?:st|nd|rd|th)\s+([A-Za-z]+)\s+(\d{4})/i);
  if (!m) return null;
  const mo = MONTHS[m[2].substring(0, 3).toUpperCase()];
  return mo ? `${m[3]}-${mo}-${m[1].padStart(2, '0')}` : null;
};

// "25/06/26" → YYYY-MM-DD
const dmyShort = (s) => {
  const m = String(s).match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (!m) return null;
  let [, d, mo, y] = m;
  if (y.length === 2) y = '20' + y;
  return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
};

export function parseEI(pages) {
  let stmtYearFrom = null, stmtYearTo = null;
  const summary = {
    statementDate: null, dueDate: null,
    minimumPayment: null, creditLimit: null, availableLimit: null, closingBalance: null,
    previousBalance: null, cashbackBalance: null, cashbackEarned: null, cashbackRedeemed: null,
  };

  // Previous balance = the dateless "OPENING BALANCE" line atop the tx table.
  for (const page of pages) {
    const op = page.find(i => /^OPENING$/i.test(i.s));
    if (!op) continue;
    const amt = page.filter(c => Math.abs(c.y - op.y) <= 4 && c.x > 480 && /^[\d,]+\.\d{2}(CR)?$/.test(c.s))
      .sort((a, b) => a.x - b.x).pop();
    if (amt) { const { amount, credit } = amountWithSign(amt.s); summary.previousBalance = credit ? -amount : amount; break; }
  }

  for (const page of pages) {
    // Statement period headers: "From:" and "To:" with adjacent ordinal date
    for (const lbl of page.filter(i => i.s === 'From:' || i.s === 'To:')) {
      const dateItem = page.find(i =>
        Math.abs(i.y - lbl.y) <= 5 && i.x > lbl.x && /\d{4}/.test(i.s));
      if (!dateItem) continue;
      const d = parseOrdinal(dateItem.s);
      if (!d) continue;
      if (lbl.s === 'From:' && !stmtYearFrom) stmtYearFrom = +d.slice(0, 4);
      if (lbl.s === 'To:' && !stmtYearTo) { stmtYearTo = +d.slice(0, 4); summary.statementDate = d; }
    }

    // Summary footer row: 5+ numerics in the y≈248–262 band (repeats every page)
    if (!summary.creditLimit) {
      const band = page.filter(i => i.y >= 248 && i.y <= 262 && /^[\d,]+\.\d{2}$/.test(i.s))
        .sort((a, b) => a.x - b.x);
      if (band.length >= 5) {
        // x order: Card Limit, Available Limit, Min Payment Due, Total Payment Due, Profit, Current Balance
        summary.creditLimit      = toNum(band[0].s);
        summary.availableLimit   = toNum(band[1].s);
        summary.minimumPayment   = toNum(band[2].s);
        summary.closingBalance   = toNum(band[band.length - 1].s);
      }
      const dueDateItem = page.find(i =>
        i.y >= 248 && i.y <= 262 && /^\d{2}\/\d{2}\/\d{2,4}$/.test(i.s));
      if (dueDateItem) summary.dueDate = dmyShort(dueDateItem.s);
    }
  }

  // Rewards Summary box: "CASHBACK <OPENING|EARNED|ADJUSTED|REDEEMED|CLOSING …> <value>"
  // — label on the left, a single integer/decimal value to its right on the same
  // row. Join each row's text (tokenization-agnostic) and pull the trailing number.
  // The closing balance is in AED and is the authoritative cashback balance.
  for (const page of pages) {
    const byY = {};
    for (const it of page) { const k = Math.round(it.y / 3) * 3; (byY[k] = byY[k] || []).push(it); }
    for (const k of Object.keys(byY)) {
      const cells = byY[k].sort((a, b) => a.x - b.x);
      const text = cells.map(c => c.s).join(' ');
      if (!/CASHBACK/i.test(text)) continue;
      const numCell = [...cells].reverse().find(c => /^-?[\d,]+(\.\d+)?$/.test(c.s));
      if (!numCell) continue;
      const v = toNum(numCell.s);
      if (/CASHBACK\s+CLOSING\s+BALANCE/i.test(text)) summary.cashbackBalance = v;
      else if (/CASHBACK\s+EARNED/i.test(text)) summary.cashbackEarned = v;
      else if (/CASHBACK\s+REDEEMED/i.test(text)) summary.cashbackRedeemed = v;
    }
  }

  const stmtYear = stmtYearTo || new Date().getFullYear();

  const txns = [];
  for (const page of pages) {
    const byY = {};
    for (const it of page) { const k = Math.round(it.y / 3) * 3; (byY[k] = byY[k] || []).push(it); }
    for (const y of Object.keys(byY).map(Number).sort((a, b) => b - a)) {
      const cells = byY[y].sort((a, b) => a.x - b.x);
      if (cells.length < 4) continue;
      // Post-date pattern: DD at x≈47, MMM at x≈61
      if (cells[0].x > 55 || !/^\d{1,2}$/.test(cells[0].s)) continue;
      if (!cells[1] || cells[1].x > 72 || !/^[A-Z]{3}$/i.test(cells[1].s)) continue;
      // Trxn-date pattern: DD at x≈103, MMM at x≈118
      if (!cells[2] || cells[2].x > 115 || !/^\d{1,2}$/.test(cells[2].s)) continue;
      if (!cells[3] || cells[3].x > 132 || !/^[A-Z]{3}$/i.test(cells[3].s)) continue;

      const amountCell = [...cells].reverse().find(c => c.x > 490 && /^[\d,]+\.\d{2}(CR)?$/.test(c.s));
      if (!amountCell) continue;

      // Description: cells from x≈145 to x<262 (excludes city@262 and country@330)
      const descCells = cells.filter(c => c.x >= 145 && c.x < 262 && c !== amountCell);
      const desc = descCells.map(c => c.s).join(' ').replace(/\s{2,}/g, ' ').trim();
      if (!desc) continue;

      // Skip structural rows (opening balance, card header)
      if (/^(OPENING|PRIMARY)$/i.test(desc.split(' ')[0])) continue;

      // Year accounting for Dec→Jan crossover
      const postMo = MONTHS[cells[1].s.toUpperCase()];
      const trxnMo = MONTHS[cells[3].s.toUpperCase()];
      if (!postMo) continue;
      const postYear = (postMo === '12' && stmtYearFrom && stmtYearFrom < stmtYear) ? stmtYearFrom : stmtYear;
      const trxnYear = (trxnMo === '12' && stmtYearFrom && stmtYearFrom < stmtYear) ? stmtYearFrom : stmtYear;

      // Column order is posting-date FIRST (DD@47 MMM@61), transaction-date
      // SECOND (DD@103 MMM@118) — the reverse of the CR-suffix banks. `date`
      // must be the real transaction date (what the user logged in Floosi);
      // the posting date is the bank's clearing lag and is only kept for display.
      const postingDate = `${postYear}-${postMo}-${cells[0].s.padStart(2, '0')}`;
      const date        = trxnMo ? `${trxnYear}-${trxnMo}-${cells[2].s.padStart(2, '0')}` : postingDate;

      const { amount, credit } = amountWithSign(amountCell.s);
      if (amount == null) continue;

      // Foreign txs print "<foreignAmount> <CCY>" between the country (x≈335)
      // and the AED amount (x≈521) — e.g. "125.00 MAD" — with a
      // "*(1 AED = MAD 2.40801)" rate line on the band below. Capture the
      // original currency/amount (currency stays AED, like the other banks).
      let originalCurrency = null, originalAmount = null;
      const fxCcyCell = cells.find(c => c.x > 360 && c.x < 430 && /^[A-Z]{3}$/.test(c.s) && c.s !== 'AED' && c !== amountCell);
      if (fxCcyCell) {
        const fxAmtCell = cells.find(c => c.x > 340 && c.x < fxCcyCell.x && /^[\d,]+\.\d{2}$/.test(c.s));
        if (fxAmtCell) { originalCurrency = fxCcyCell.s; originalAmount = toNum(fxAmtCell.s); }
      }

      const tag = /TRANSFER.*PAYMENT.*RECEIVED|PAYMENT.*RECEIVED.*THANK/i.test(desc) ? 'payment'
        : /CASHBACK.*REDEMPTION|REDEMPTION/i.test(desc) ? 'cashback'
        : /DISCRETIONARY.*REBATE|PROFIT.*REBATE/i.test(desc) ? 'islamic_net'
        : null;

      txns.push({ date, postingDate, description: desc, amount, credit, currency: 'AED', originalCurrency, originalAmount, tag });
    }
  }

  return { bank: 'EI', summary, transactions: txns };
}
